import { useToast } from '@chakra-ui/react';
import { ERROR_TOAST_DEFAULTS } from '@helpers';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import { Observer } from './Observer';
import { ObserverModal } from './ObserverModal';
import { useFetchAll as useFetchContacts } from '@pages/ContactsPage/hooks';
import { Select } from '@design-system/inputs/BaseInputs';
import { IContact } from '@cincel/client';

export type ObserverType = {
  id: string;
  name: string;
  email: string;
};

export type ObserversTabProps = {
  color: string;
  isActive?: boolean;
  observers: ObserverType[];
  signing?: boolean;
  setObservers: (
    data: ObserverType | ObserverType[],
    type?: string,
    edited?: ObserverType,
  ) => void;
  actionsEnabled?: boolean;
};
export const ObserversTab: React.FC<ObserversTabProps> = ({
  color,
  observers,
  actionsEnabled = true,
  signing = false,
  setObservers,
}) => {
  const { t } = useTranslation();
  const toast = useToast();

  const [observer, setObserver] = useState<ObserverType>();
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const onNewObserver = () => {
    setObserver(undefined);
    setShowModal(true);
    setIsEditing(false);
  };

  const onDeleteObserver = (index) => {
    const newObservers = [...observers];
    const observerToDelete = newObservers[index];
    newObservers.splice(index, 1);
    newObservers.map((_, i) => ({
      ...newObservers,
      id: i,
    }));
    setObservers([...newObservers], 'delete', observerToDelete);
  };

  const onEditObserver = (index) => {
    const observerValues = observers[index];

    if (observerValues) {
      setShowModal(true);
      setIsEditing(true);
      setObserver(observerValues);
    }
  };

  const handleSuccess = (data) => {
    if (isEditing) {
      const oldObservers = observers;

      const existingObserver = observers.find(
        (observer) => data.email === observer.email,
      );

      if (
        existingObserver?.email === data.email &&
        existingObserver?.name === data.name
      ) {
        toast({
          ...ERROR_TOAST_DEFAULTS,
          title: t(translations.ERRORS.EXISTING_EMAIL),
        });

        return;
      }

      const index = oldObservers.findIndex((o) => o.id === observer?.id);
      if (index > -1) {
        const newObservers = [...oldObservers];
        newObservers[index] = {
          id: oldObservers[index].id,
          ...data,
        };

        setObservers(newObservers, 'edit', {
          id: oldObservers[index].id,
          ...data,
        });
      }
    } else {
      const existingObserver = observers.find(
        (observer) => data.email === observer.email,
      );

      if (existingObserver) {
        toast({
          ...ERROR_TOAST_DEFAULTS,
          title: t(translations.ERRORS.EXISTING_EMAIL),
        });

        return;
      }
      setObservers(
        {
          id: observers.length + 1,
          ...data,
        },
        'create',
      );
    }

    setShowModal(false);
  };

  const handleSetObservers = (o) => {
    const oData = {
      id: observer?.id,
      ...o,
    };

    setObserver(oData);
  };
  const { data: contacts } = useFetchContacts({ pageSize: 100000 });

  const renderFromContacts = () => {
    return (
      <React.Fragment>
        <p className="pb-2 text-sm font-medium">
          {t(translations.MODALS.ADD_FROM_CONTACTS)}
        </p>
        <Select
          className="mb-4 flex-1"
          isMulti
          options={contacts?.data?.map((item: IContact) => ({
            id: item.uuid,
            label: item?.email,
            value: item,
          }))}
          onChange={(next) => {
            setObservers(next, 'create');
          }}
          placeholder={t(translations.MODALS.CHOOSE_SIGNERS)}
          primaryColor={color}
          size="md"
          value={observers}
          maxMenuHeight={70}
        />
      </React.Fragment>
    );
  };

  return (
    <div>
      <div>
        
        {actionsEnabled && <div>{renderFromContacts()}</div>}

        <div className="mt-8 first:mt-4">
          <div className="rounded-2xl border-2 border-gray-100 bg-basic-white px-4 py-5">
            {!observers.length && (
              <div>
                <p>{t(translations.DOCUMENT_PAGES.NO_OBSERVERS)}</p>
              </div>
            )}
            {observers.map(({ name, email }, observerIndex) => {
              return (
                <>
                  <Observer
                    key={observerIndex}
                    name={name}
                    email={email}
                    handleDeleteObserver={onDeleteObserver}
                    handleEditObserver={onEditObserver}
                    index={observerIndex}
                    color={color}
                    signing={signing}
                    actionsEnabled={actionsEnabled}
                  />
                  <hr className="my-4 border border-dashed border-gray-100 md:min-w-full" />
                </>
              );
            })}

            {!signing && actionsEnabled && (
              <div className="flex">
                <button
                  className="mx-auto h-12 w-12 rounded-full border text-basic-white focus:outline-none"
                  style={{ background: color }}
                  onClick={() => onNewObserver()}
                >
                  +
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
      <ObserverModal
        color={color}
        setShowModal={() => setShowModal((p) => !p)}
        isOpen={showModal}
        onSuccess={handleSuccess}
        isEditing={isEditing}
        observer={observer}
        setObserver={handleSetObservers}
      />
    </div>
  );
};
